import React, { useReducer } from 'react'
import PropTypes from 'prop-types'
import HandicapHistoryRoot from '../member_handicap_history/HandicapHistoryRoot'
import { INITIAL_STATE, summaryReducer, getFilteredPlayers, setSearchQuery, setSelectedPlayer } from './reducer'
import useLoadSummary from './hooks/useLoadSummary'
import { getFontColor, hasHandicap, playerSummaryUrl, t } from './helpers'

const HandicapServerSummaryRoot = ({ dataUrl, playerSummaryBaseUrl, portalColor, whsTerms }) => {
  const [state, dispatch] = useReducer(summaryReducer, INITIAL_STATE)
  const { loading, error, summary, searchQuery, selectedPlayer } = state

  useLoadSummary(dataUrl, dispatch, (e) => (
    // 502 is what summary_data returns when the Handicap Server is unreachable.
    e.status === 502
      ? t('handicap_server_summary.service_unavailable')
      : t('handicap_server_summary.failed_to_load')
  ))

  // Dynamic portal theming: the CSS classes read var(--hs-brand-color) with a
  // hex fallback, so we only inject the custom property when a portal color exists.
  const brandStyle = {}
  if (portalColor) {
    brandStyle['--hs-brand-color'] = portalColor
    const brandText = getFontColor(portalColor)
    if (brandText) brandStyle['--hs-brand-text'] = brandText
  }

  if (selectedPlayer) {
    return (
      <div className="handicap-server-summary" style={brandStyle}>
        <div className="hss-back-bar">
          <button className='back-btn' onClick={() => setSelectedPlayer(dispatch, null)}>
            <i className='fa fa-arrow-left' />
            {t('handicap_server_summary.back_to_players')}
          </button>
          <h2>{selectedPlayer.name}</h2>
        </div>
        <HandicapHistoryRoot
          key={selectedPlayer.id}
          handicapSummaryUrl={playerSummaryUrl(playerSummaryBaseUrl, selectedPlayer.id)}
          readOnly={true}
          whsTerms={whsTerms}
        />
      </div>
    )
  }

  if (loading) {
    return (
      <div className="hss-center-state">
        <i className="fa fa-spinner fa-spin fa-2x" />
        <p>{t('handicap_server_summary.loading')}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="hss-error">
        <div className="alert alert-danger"><strong>{t('handicap_server_summary.error')}</strong> {error}</div>
      </div>
    )
  }

  if (!summary) return null

  const { show_9, show_18, league_name, players } = summary

  const subtitle = show_9 && show_18
    ? t('handicap_server_summary.subtitle_both')
    : show_18 ? t('handicap_server_summary.subtitle_18') : t('handicap_server_summary.subtitle_9')

  const colSpan = 3 + (show_9 ? 1 : 0) + (show_18 ? 1 : 0)

  const filteredPlayers = getFilteredPlayers(players, searchQuery)

  return (
    <div className="handicap-server-summary" style={brandStyle}>
      <div className="hss-header">
        <h2>{league_name}</h2>
        <p className="subtitle">{subtitle}</p>
      </div>

      <div className="hss-search-row">
        <div className="search-wrap">
          <i className="fa fa-search search-icon" />
          <input
            type="text"
            placeholder={t('handicap_server_summary.search_placeholder')}
            value={searchQuery}
            onChange={e => setSearchQuery(dispatch, e.target.value)}
          />
          {searchQuery && (
            <button className="clear-btn" onClick={() => setSearchQuery(dispatch, '')} title={t('handicap_server_summary.clear')}>
              <i className="fa fa-times" />
            </button>
          )}
        </div>
        {searchQuery && (
          <span className="count-label">
            {t('handicap_server_summary.count_label', { filtered: filteredPlayers.length, total: players.length })}
          </span>
        )}
      </div>

      <div className="table-responsive">
        <table className="hss-table">
          <thead>
            <tr>
              <th>{t('handicap_server_summary.col_name')}</th>
              {show_9 && (
                <th className="hdcp-col">{t('handicap_server_summary.col_9_hole')}{' '}<span className="hdcp-word">{t('handicap_server_summary.handicap')}</span><span className="hdcp-abbr">{t('handicap_server_summary.hdcp')}</span></th>
              )}
              {show_18 && (
                <th className="hdcp-col">{t('handicap_server_summary.col_18_hole')}{' '}<span className="hdcp-word">{t('handicap_server_summary.handicap')}</span><span className="hdcp-abbr">{t('handicap_server_summary.hdcp')}</span></th>
              )}
              <th>{t('handicap_server_summary.scores')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filteredPlayers.length === 0 ? (
              <tr>
                <td className="hss-empty-cell" colSpan={colSpan}>
                  {searchQuery ? t('handicap_server_summary.no_match', { query: searchQuery }) : t('handicap_server_summary.no_players')}
                </td>
              </tr>
            ) : (
              filteredPlayers.map(player => (
                <tr key={player.id}>
                  <td className="player-name">{player.name}</td>
                  {show_9 && (
                    <td className={hasHandicap(player.handicap_9_view) ? 'handicap-val' : 'muted'}>
                      {hasHandicap(player.handicap_9_view) ? player.handicap_9_view : '-'}
                    </td>
                  )}
                  {show_18 && (
                    <td className={hasHandicap(player.handicap_view) ? 'handicap-val' : 'muted'}>
                      {hasHandicap(player.handicap_view) ? player.handicap_view : '-'}
                    </td>
                  )}
                  <td>
                    <span className={player.score_count === 0 ? 'muted' : undefined}>{player.score_count}</span>
                  </td>
                  <td>
                    {player.id && (
                      <button className='details-btn' onClick={() => setSelectedPlayer(dispatch, player)}>
                        {t('handicap_server_summary.details')}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="hss-footer">
        {t('handicap_server_summary.players_total', { count: players.length })}
      </div>

    </div>
  )
}

HandicapServerSummaryRoot.propTypes = {
  dataUrl: PropTypes.string.isRequired,
  playerSummaryBaseUrl: PropTypes.string.isRequired,
  portalColor: PropTypes.string,
  whsTerms: PropTypes.object,
}

export default HandicapServerSummaryRoot
